import React, { useCallback, useEffect } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import { Box, Button, Flex } from '@driftt/dds'

import { DateRange } from 'components/DateRangePicker'
import { ErrorBoundary } from 'components/ErrorBoundary'
import {
  driftEmptyStateBuilder,
  driftErrorStateBuilder,
  driftLoadingStateBuilder
} from 'components/States'
import { useActivityTimeline } from 'hooks/useActivityTimeline'
import { accountPath } from 'utils/paths'

import ActivityGroup from './ActivityGroup'

import './styles.css'

interface ActivityFeedProps {
  accountId?: string
  contactId?: string
  trackAction: (action: string, additionalData?: any) => void
  inDrawer: boolean
  scrollTargetId?: string
  dateRange?: DateRange
  setDateRange?: (dateRange: DateRange | undefined) => void
}
export const TimelineEventsFeed: React.FC<ActivityFeedProps> = ({
  accountId,
  contactId,
  trackAction,
  inDrawer,
  scrollTargetId,
  dateRange,
  setDateRange
}) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => trackAction('Activity feed viewed'), [])

  const {
    endUserData,
    data,
    error,
    isLoading,
    hasMoreActivity,
    fetchNextPage,
    dataTotal
  } = useActivityTimeline(inDrawer, dateRange, accountId, contactId)

  const LoadingComponent = driftLoadingStateBuilder('Loading activity')
  const ErrorComponent = driftErrorStateBuilder('account activity')

  const EmptyComponent = driftEmptyStateBuilder(
    inDrawer ? 'No account activity lately' : 'No account activity yet',
    inDrawer
      ? "There's been no account activity from the last two weeks. See all account activity in the account details page."
      : 'Once your account visits your site, it will show up here.'
  )
  const LoadingMoreComponent = () => (
    <Box
      css={{
        backgroundColor: '$gray1',
        borderRadius: '3px',
        padding: '16px 32px',
        zIndex: 1
      }}>
      <LoadingComponent />
    </Box>
  )

  const showAllActivity = useCallback(() => {
    if (inDrawer && accountId) {
      window.open(accountPath(accountId), '_blank')
    } else {
      setDateRange && setDateRange(undefined)
    }
  }, [accountId, inDrawer, setDateRange])

  return (
    <Flex
      id="drawerActivityFeed"
      direction="column"
      fullHeight
      fullWidth
      alignItems="center"
      css={{
        backgroundColor: inDrawer ? '$gray2' : 'inherit',
        padding: '32px 16px',
        height: 'calc(100% - 31px)',
        overflow: 'scroll'
      }}>
      <ErrorBoundary errorState={<ErrorComponent />}>
        <InfiniteScroll
          scrollableTarget={scrollTargetId ?? 'drawerActivityFeed'}
          next={fetchNextPage}
          hasMore={!!hasMoreActivity}
          loader={<LoadingMoreComponent />}
          dataLength={dataTotal}
          onScroll={() => trackAction('Feed scroll')}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {isLoading && !data && !error && <LoadingComponent />}
          {error && !isLoading && <ErrorComponent />}
          {!isLoading && (!data || Object.keys(data).length < 1) && !error && (
            <Box css={{ marginBottom: '16px' }}>
              <EmptyComponent />
            </Box>
          )}
          {/* Activity Feed */}
          {data && dataTotal > 0 && (
            <>
              {/* vertical line */}
              <Box
                css={{
                  position: inDrawer ? 'fixed' : 'absolute',
                  height: inDrawer
                    ? '100%'
                    : accountId
                    ? 'calc(100% - 450px)'
                    : 'calc(100% + 80px)',
                  minHeight: accountId ? '1184px' : 'calc(100vh - 225px)',
                  width: '1px',
                  backgroundColor: '$gray4',
                  left: '50%',
                  top: contactId ? 0 : 'inherit',
                  zIndex: '0',
                  transform: 'translateY(-32px)'
                }}
              />
              {/* activities by day */}
              {Object.values(data).map(activities => {
                const date = activities[0].createdAt
                return (
                  <ActivityGroup
                    key={date}
                    endUserData={endUserData}
                    activities={activities}
                    trackAction={trackAction}
                  />
                )
              })}
            </>
          )}
          {!isLoading && !error && (inDrawer || dateRange) && (
            <Button onClick={showAllActivity}>See all account activity</Button>
          )}
        </InfiniteScroll>
      </ErrorBoundary>
    </Flex>
  )
}

export default TimelineEventsFeed
